import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { KeyRound, Settings2, PlugZap, CheckCircle2, XCircle, ExternalLink, Archive, PlayCircle, Mail, Database, Cloud, Users, Trash2, Plus, RefreshCw, Image as ImageIcon, ChevronDown, Check, AlertTriangle } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function BackupScheduleSection() {
  const [sched, setSched] = useState(null);
  const [form, setForm] = useState({ enabled: false, hour_utc: 6, retain: 14 });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [snapshots, setSnapshots] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([
        api.get("/settings/backup-schedule"),
        api.get("/admin/backup/snapshots"),
      ]);
      setSched(s.data);
      setForm({ enabled: !!s.data.enabled, hour_utc: s.data.hour_utc, retain: s.data.retain });
      setSnapshots(list.data || []);
    } catch { /* admin only */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/backup-schedule", form);
      setSched(data);
      toast.success("Schedule saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/admin/backup/run-now");
      toast.success("Snapshot written");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };
  const download = async (name) => {
    try {
      const r = await api.get(`/admin/backup/snapshots/${name}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a"); a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (name) => {
    if (!window.confirm(`Delete snapshot ${name}?`)) return;
    try {
      await api.delete(`/admin/backup/snapshots/${name}`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!sched) return null;
  return (
    <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-700" data-testid="backup-schedule">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-sm font-semibold text-zinc-950 dark:text-white">Nightly backups &amp; retention</h3>
          <p className="text-[11px] text-zinc-500 dark:text-zinc-300 mt-0.5">Snapshots are written to <code className="dark:text-white">backend/backups/</code> and rotated automatically. Last run: {sched.last_run_at ? new Date(sched.last_run_at).toLocaleString() : "never"} · {sched.last_status || "—"}</p>
        </div>
        <Button onClick={runNow} disabled={running} data-testid="backup-run-now" variant="outline"
          className="h-9">
          {running ? "Running…" : "Run backup now"}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="flex items-center gap-2">
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                  data-testid="backup-enabled-switch" />
          <Label className="text-xs text-zinc-950 dark:text-white">Enable nightly backup</Label>
        </div>
        <div>
          <Label className="text-xs text-zinc-950 dark:text-white">Hour (UTC)</Label>
          <Input type="number" min={0} max={23} value={form.hour_utc}
            onChange={(e) => setForm((f) => ({ ...f, hour_utc: Number(e.target.value) }))}
            data-testid="backup-hour-input" className="mt-1 text-zinc-950 dark:text-white" />
        </div>
        <div>
          <Label className="text-xs text-zinc-950 dark:text-white">Retain last N</Label>
          <Input type="number" min={1} max={365} value={form.retain}
            onChange={(e) => setForm((f) => ({ ...f, retain: Number(e.target.value) }))}
            data-testid="backup-retain-input" className="mt-1 text-zinc-950 dark:text-white" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="backup-schedule-save"
          className="h-11 bg-[#0055FF] hover:bg-[#0040CC] text-white">
          {saving ? "Saving…" : "Save schedule"}
        </Button>
      </div>
      {snapshots.length > 0 && (
        <div className="mt-4 border border-zinc-200 rounded-md divide-y divide-zinc-100 dark:border-zinc-700 dark:divide-zinc-700">
          {snapshots.map((s) => (
            <div key={s.name} className="flex items-center justify-between px-3 py-2 text-xs">
              <div className="font-mono text-zinc-800 dark:text-white">{s.name}</div>
              <div className="text-zinc-500 flex items-center gap-4">
                <span>{(s.size / 1024 / 1024).toFixed(2)} MB</span>
                <span>{new Date(s.mtime).toLocaleString()}</span>
                <button onClick={() => download(s.name)} data-testid={`snapshot-download-${s.name}`}
                        className="text-[#0055FF] hover:underline">Download</button>
                <button onClick={() => del(s.name)} data-testid={`snapshot-delete-${s.name}`}
                        className="text-red-600 hover:underline">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const [sso, setSso] = useState(null);
  const [form, setForm] = useState({ tenant_id: "", client_id: "", client_secret: "", redirect_uri: "", enabled: false, allowed_domains: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Archive settings
  const [arch, setArch] = useState(null);
  const [archForm, setArchForm] = useState({ month: 7, day: 1, enabled: false, only_closed: true });
  const [archSaving, setArchSaving] = useState(false);
  const [archRunning, setArchRunning] = useState(false);

  // Cloudflare reverse proxy settings
  const [cf, setCf] = useState(null);
  const [cfForm, setCfForm] = useState({ enabled: false, public_hostname: "", tunnel_token: "", trust_cf_headers: true, additional_origins: "" });
  const [cfSaving, setCfSaving] = useState(false);

  // SMTP smarthost settings
  const [smtp, setSmtp] = useState(null);
  const [smtpForm, setSmtpForm] = useState({
    host: "", port: 587, username: "", password: "",
    from_email: "", from_name: "ESUSD HelpDesk",
    security: "starttls", enabled: false, emergent_proxy_enabled: true, test_to: "",
  });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [smtpLog, setSmtpLog] = useState([]);
  const [smtpLogRefreshing, setSmtpLogRefreshing] = useState(false);

  // SSO Group Sync
  const [ssoSyncMaps, setSsoSyncMaps] = useState([]);
  const [ssoSyncSchedule, setSsoSyncSchedule] = useState(null);
  const [ssoSyncLog, setSsoSyncLog] = useState([]);
  const [ssoGraphStatus, setSsoGraphStatus] = useState(null);
  const [ssoSyncRunning, setSsoSyncRunning] = useState(false);
  const [ssoSyncPreview, setSsoSyncPreview] = useState(null);   // { created:[], updated:[], cleared:[], errors:[] }
  const [ssoSyncPreviewing, setSsoSyncPreviewing] = useState(false);
  const [ssoSyncGroupSearch, setSsoSyncGroupSearch] = useState("");
  const [ssoSyncSearchResults, setSsoSyncSearchResults] = useState([]);
  const [ssoSyncSearching, setSsoSyncSearching] = useState(false);
  const SITE_CODES = ["District", "LVHS", "LVES", "BES", "EBES", "CHS", "AES"];
  const ROLE_LIST = ["user", "teacher", "secretary", "mechanic", "custodian", "maintenance", "it", "principal", "admin"];

  // Login page branding
  const [loginLogo, setLoginLogo] = useState(null);       // { data_url, updated_at, updated_by }
  const [logoUploading, setLogoUploading] = useState(false);

  // Maintenance Mode — global email suppression + banner
  const [mm, setMm] = useState(null);                       // { enabled, reason, updated_at, updated_by }
  const [mmReason, setMmReason] = useState("");
  const [mmSaving, setMmSaving] = useState(false);

  // Settings table-of-contents active-section tracker
  const [activeSection, setActiveSection] = useState("section-login-logo");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/sso");
      setSso(data);
      setForm({
        tenant_id: data.tenant_id || "",
        client_id: data.client_id || "",
        client_secret: "",
        redirect_uri: data.redirect_uri || data.suggested_redirect_uri || "",
        enabled: !!data.enabled,
        allowed_domains: (data.allowed_domains || []).join(", "),
      });
    } catch (e) { toast.error(formatApiError(e)); }
    try {
      const { data } = await api.get("/settings/archive");
      setArch(data);
      setArchForm({ month: data.month, day: data.day, enabled: !!data.enabled, only_closed: !!data.only_closed });
    } catch { /* admin-only endpoint, ignore for non-admin */ }
    try {
      const { data } = await api.get("/settings/cloudflare");
      setCf(data);
      setCfForm({
        enabled: !!data.enabled,
        public_hostname: data.public_hostname || "",
        tunnel_token: data.tunnel_token_masked || "",
        trust_cf_headers: !!data.trust_cf_headers,
        additional_origins: (data.additional_origins || []).join(", "),
      });
    } catch { /* admin-only */ }
    try {
      const { data } = await api.get("/settings/smtp");
      setSmtp(data);
      setSmtpForm((f) => ({
        ...f,
        host: data.host || "",
        port: data.port || 587,
        username: data.username || "",
        password: "",
        from_email: data.from_email || "",
        from_name: data.from_name || "ESUSD HelpDesk",
        security: data.security || "starttls",
        enabled: !!data.enabled,
        emergent_proxy_enabled: data.emergent_proxy_enabled === true,
      }));
    } catch { /* admin-only */ }
    try {
      const { data } = await api.get("/settings/smtp/log", { params: { limit: 20 } });
      setSmtpLog(data.entries || []);
    } catch { /* admin-only */ }
    try {
      const [maps, sched, log, gs] = await Promise.all([
        api.get("/settings/sso/group-map"),
        api.get("/settings/sso/sync-schedule"),
        api.get("/settings/sso/sync-log", { params: { limit: 20 } }),
        api.get("/settings/sso/graph-status"),
      ]);
      setSsoSyncMaps(maps.data || []);
      setSsoSyncSchedule(sched.data);
      setSsoSyncLog(log.data.entries || []);
      setSsoGraphStatus(gs.data);
    } catch { /* admin-only */ }
    try {
      const { data } = await api.get("/settings/login-logo");
      setLoginLogo(data);
    } catch { /* public endpoint — should always succeed */ }
    try {
      const { data } = await api.get("/settings/maintenance-mode");
      setMm(data);
      setMmReason(data.reason || "");
    } catch { /* admin-only */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ---- Maintenance Mode handlers ----
  const toggleMaintenance = async (nextEnabled) => {
    if (nextEnabled && !mm?.enabled) {
      if (!window.confirm(
        "Turn ON Maintenance Mode?\n\nWhile ON:\n · All outbound email alerts are SUPPRESSED (users won't get notified of ticket updates)\n · A red banner appears across the app for everyone\n · The app stays fully usable — only emails are silenced\n\nThe action and reason are recorded to the audit log."
      )) return;
    }
    setMmSaving(true);
    try {
      const { data } = await api.put("/settings/maintenance-mode", {
        enabled: !!nextEnabled,
        reason: mmReason.trim(),
      });
      setMm(data);
      setMmReason(data.reason || "");
      toast.success(data.enabled ? "Maintenance Mode is ON — emails paused" : "Maintenance Mode is OFF — emails resumed");
      // Notify the AppShell banner immediately so the user doesn't need to reload
      window.dispatchEvent(new CustomEvent("maintenance-mode-changed", { detail: data }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMmSaving(false); }
  };

  const saveMaintenanceReason = async () => {
    if (!mm) return;
    setMmSaving(true);
    try {
      const { data } = await api.put("/settings/maintenance-mode", { reason: mmReason.trim() });
      setMm(data);
      toast.success("Reason updated");
      window.dispatchEvent(new CustomEvent("maintenance-mode-changed", { detail: data }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMmSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // parse allowed_domains from CSV string → list
      payload.allowed_domains = (form.allowed_domains || "")
        .split(",")
        .map((d) => d.trim().replace(/^@/, "").toLowerCase())
        .filter(Boolean);
      // Only send client_secret if user typed something new
      if (!payload.client_secret) delete payload.client_secret;
      const { data } = await api.put("/settings/sso", payload);
      setSso(data);
      toast.success("SSO settings saved");
      setForm((f) => ({ ...f, client_secret: "", allowed_domains: (data.allowed_domains || []).join(", ") }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/sso/test");
      setTestResult(data);
      if (data.ok) toast.success("Microsoft metadata reachable"); else toast.error(data.detail || "Test failed");
    } catch (e) {
      setTestResult({ ok: false, detail: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally { setTesting(false); }
  };

  const saveArchive = async () => {
    setArchSaving(true);
    try {
      const { data } = await api.put("/settings/archive", archForm);
      setArch(data);
      toast.success("Archive schedule saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setArchSaving(false); }
  };

  const runArchive = async () => {
    if (!confirm("Archive all Closed/Resolved tickets now? They'll be hidden from queues (audit log records the run).")) return;
    setArchRunning(true);
    try {
      const { data } = await api.post("/settings/archive/run");
      toast.success(`${data.archived} ticket${data.archived === 1 ? "" : "s"} archived`);
      const r = await api.get("/settings/archive");
      setArch(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setArchRunning(false); }
  };

  const saveSmtp = async () => {
    setSmtpSaving(true);
    try {
      const payload = {
        host: smtpForm.host.trim(),
        port: parseInt(smtpForm.port, 10) || 587,
        username: smtpForm.username.trim(),
        from_email: smtpForm.from_email.trim(),
        from_name: smtpForm.from_name.trim(),
        security: smtpForm.security,
        enabled: !!smtpForm.enabled,
        emergent_proxy_enabled: !!smtpForm.emergent_proxy_enabled,
      };
      // Only send password when the user typed a new one
      if (smtpForm.password && !smtpForm.password.startsWith("•")) {
        payload.password = smtpForm.password;
      }
      const { data } = await api.put("/settings/smtp", payload);
      setSmtp(data);
      setSmtpForm((f) => ({ ...f, password: "" }));
      toast.success("SMTP settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSmtpSaving(false); }
  };

  const testSmtp = async () => {
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const payload = {
        host: smtpForm.host.trim(),
        port: parseInt(smtpForm.port, 10) || 587,
        username: smtpForm.username.trim(),
        from_email: smtpForm.from_email.trim(),
        from_name: smtpForm.from_name.trim(),
        security: smtpForm.security,
        test_to: smtpForm.test_to.trim() || undefined,
      };
      if (smtpForm.password && !smtpForm.password.startsWith("•")) {
        payload.password = smtpForm.password;
      }
      const { data } = await api.post("/settings/smtp/test", payload);
      setSmtpTestResult(data);
      if (data.ok) toast.success(`Test email sent to ${data.sent_to}`);
      else toast.error(data.detail || "SMTP test failed");
      // Refresh log so the new attempt appears
      try {
        const r = await api.get("/settings/smtp/log", { params: { limit: 20 } });
        setSmtpLog(r.data.entries || []);
      } catch { /* non-fatal */ }
    } catch (e) {
      setSmtpTestResult({ ok: false, detail: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally { setSmtpTesting(false); }
  };

  const refreshSmtpLog = async () => {
    setSmtpLogRefreshing(true);
    try {
      const { data } = await api.get("/settings/smtp/log", { params: { limit: 20 } });
      setSmtpLog(data.entries || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSmtpLogRefreshing(false); }
  };

  // ---- SSO Sync handlers ----
  const searchSsoGroups = async () => {
    if (!ssoSyncGroupSearch.trim() || ssoSyncGroupSearch.trim().length < 2) {
      toast.error("Enter at least 2 characters");
      return;
    }
    setSsoSyncSearching(true);
    try {
      const { data } = await api.get("/settings/sso/groups/search", { params: { q: ssoSyncGroupSearch.trim() } });
      setSsoSyncSearchResults(data.value || []);
      if (!(data.value || []).length) toast.info("No matching Entra groups");
    } catch (e) { toast.error(formatApiError(e)); setSsoSyncSearchResults([]); }
    finally { setSsoSyncSearching(false); }
  };

  const addSsoGroupMap = async (group) => {
    try {
      const { data } = await api.post("/settings/sso/group-map", {
        group_id: group.id,
        group_display_name: group.displayName,
        site: null,
        role: null,
        enabled: true,
      });
      setSsoSyncMaps((m) => [...m, data]);
      setSsoSyncGroupSearch("");
      setSsoSyncSearchResults([]);
      toast.success(`Added ${group.displayName}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patchSsoMap = async (id, patch) => {
    try {
      const { data } = await api.patch(`/settings/sso/group-map/${id}`, patch);
      setSsoSyncMaps((maps) => maps.map((m) => (m.id === id ? data : m)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSsoMap = async (id) => {
    if (!confirm("Remove this group mapping? SSO-provisioned users under this group will lose their site on next sync.")) return;
    try {
      await api.delete(`/settings/sso/group-map/${id}`);
      setSsoSyncMaps((maps) => maps.filter((m) => m.id !== id));
      toast.success("Mapping removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runSsoSync = async () => {
    if (!confirm("Run SSO group sync now?\n\nMirror-mode: SSO-provisioned users no longer in any mapped group will have their sites cleared.")) return;
    setSsoSyncRunning(true);
    try {
      const { data } = await api.post("/settings/sso/sync-run");
      if (data.ok) {
        toast.success(`Sync done — created ${data.created}, updated ${data.updated}, cleared ${data.cleared}`);
      } else {
        toast.error(`Sync failed: ${(data.errors && data.errors[0]) || "unknown"}`);
      }
      // Clear any lingering preview and refresh log
      setSsoSyncPreview(null);
      try {
        const r = await api.get("/settings/sso/sync-log", { params: { limit: 20 } });
        setSsoSyncLog(r.data.entries || []);
      } catch { /* non-fatal */ }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSsoSyncRunning(false); }
  };

  const previewSsoSync = async () => {
    setSsoSyncPreviewing(true);
    setSsoSyncPreview(null);
    try {
      const { data } = await api.post("/settings/sso/sync-preview");
      setSsoSyncPreview(data);
      if (!data.ok) toast.error(`Preview reported: ${(data.errors && data.errors[0]) || "unknown"}`);
      else if ((data.created + data.updated + data.cleared) === 0) toast.info("Preview: nothing would change");
      else toast.success(`Preview: +${data.created} / ~${data.updated} / −${data.cleared}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSsoSyncPreviewing(false); }
  };

  const updateSsoSchedule = async (patch) => {
    try {
      const { data } = await api.put("/settings/sso/sync-schedule", patch);
      setSsoSyncSchedule(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const recheckGraph = async () => {
    setSsoGraphStatus({ ...(ssoGraphStatus || {}), rechecking: true });
    try {
      const { data } = await api.get("/settings/sso/graph-status");
      setSsoGraphStatus(data);
      if (data.ok) toast.success(`Graph reachable — tenant: ${data.tenant_display_name || "unknown"}`);
      else toast.error(data.detail || "Graph unreachable");
    } catch (e) {
      setSsoGraphStatus({ ok: false, detail: formatApiError(e) });
      toast.error(formatApiError(e));
    }
  };

  // ---- Login logo handlers ----
  const onLoginLogoPicked = async (file) => {
    if (!file) return;
    if (file.size > 1_200_000) { toast.error("Logo must be under 1 MB"); return; }
    setLogoUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => rej(r.error);
        r.readAsDataURL(file);
      });
      const { data } = await api.put("/settings/login-logo", { data_url: dataUrl });
      setLoginLogo(data);
      toast.success("Login logo saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLogoUploading(false); }
  };

  const removeLoginLogo = async () => {
    if (!confirm("Remove the custom login-page logo?")) return;
    setLogoUploading(true);
    try {
      const { data } = await api.put("/settings/login-logo", { data_url: null });
      setLoginLogo(data);
      toast.success("Login logo removed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLogoUploading(false); }
  };

  // Highlight the TOC entry that matches the section currently in view
  useEffect(() => {
    const ids = ["section-maintenance", "section-login-logo", "section-sso", "section-sso-sync",
                 "section-smtp", "section-cloudflare", "section-archive", "section-backup"];
    const obs = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (visible) setActiveSection(visible.target.id);
    }, { rootMargin: "-15% 0px -70% 0px", threshold: 0 });
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [smtp, cf, arch, ssoSyncSchedule]);  // re-observe when conditionally-rendered cards mount

  // Deterministic scroll for TOC clicks — bypasses the flaky Tailwind arbitrary
  // child selector for scroll-margin, honors the sticky nav offset, and stays
  // smooth. Falls back to the native anchor jump if the target is missing.
  const scrollToSection = (id) => (e) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    const top = el.getBoundingClientRect().top + window.scrollY - 24;
    window.scrollTo({ top, behavior: "smooth" });
    setActiveSection(id);
    // Push the hash into the URL so refresh/back keep the deep-link
    if (window.history?.replaceState) window.history.replaceState(null, "", `#${id}`);
  };
  return (
    <div className="p-4 md:p-8" data-testid="settings-page">
      <div className="flex items-start gap-3">
        <Settings2 className="h-6 w-6 text-zinc-500 dark:text-zinc-400 mt-1" />
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-400">Administration · ESUSD</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 dark:text-white mt-1">Settings</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1">Configure single sign-on and other district-wide app options.</p>
        </div>
      </div>

      <div className="mt-8 lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-8">
        {/* Sticky table of contents (desktop only) */}
        <nav data-testid="settings-toc" className="hidden lg:block lg:sticky lg:top-6 lg:self-start" aria-label="Settings sections">
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-400 font-semibold mb-3">On this page</div>
          <ul className="space-y-0.5 text-sm">
            {[
              ["section-maintenance", "Maintenance Mode"],
              ["section-login-logo", "Login branding"],
              ["section-sso",        "Microsoft Entra SSO"],
              ["section-sso-sync",   "Entra group sync"],
              ["section-smtp",       "SMTP smarthost"],
              ["section-cloudflare", "Cloudflare proxy"],
              ["section-archive",    "Yearly archive"],
              ["section-backup",     "Backup & restore"],
            ].map(([id, label]) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  onClick={scrollToSection(id)}
                  data-testid={`toc-${id}`}
                  className={`block py-1.5 px-3 rounded-md transition-colors border-l-2 ${
                    activeSection === id
                      ? "bg-zinc-50 dark:bg-zinc-800 text-zinc-950 dark:text-white border-[#0055FF] font-medium"
                      : "text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 hover:text-zinc-950 dark:hover:text-white border-transparent hover:border-zinc-300 dark:hover:border-zinc-600"
                  }`}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Cards column — scroll-margin so anchor jumps don't hide behind sticky nav */}
        <div className="lg:min-w-0 [&>*[id^='section-']]:scroll-mt-6">
      {/* -------- Maintenance Mode (site-wide email kill-switch + banner) -------- */}
      <div
        id="section-maintenance"
        data-testid="maintenance-mode-card"
        className={`rounded-md p-6 mt-8 max-w-3xl border-2 ${
          mm?.enabled
            ? "bg-red-50 dark:bg-red-950/40 border-red-400 dark:border-red-800"
            : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700"
        }`}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${mm?.enabled ? "text-red-600 dark:text-red-400" : "text-zinc-500"}`} strokeWidth={2.4} />
              <h2 className="font-heading text-lg font-semibold text-zinc-950 dark:text-white">Maintenance Mode</h2>
              {mm?.enabled && (
                <span className="inline-flex items-center text-[10px] uppercase tracking-[0.12em] font-semibold bg-red-600 text-white px-2 py-0.5 rounded-full" data-testid="maintenance-mode-status-pill">
                  Active
                </span>
              )}
            </div>
            <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-1 max-w-xl leading-relaxed">
              Silences <strong>every outbound email</strong> (ticket create/assign/status/comments/close notifications) and shows a site-wide banner to all signed-in users. The app stays fully usable — only emails are paused. Every toggle is written to the audit log.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Switch
              checked={!!mm?.enabled}
              onCheckedChange={toggleMaintenance}
              disabled={mmSaving || !mm}
              data-testid="maintenance-mode-toggle"
              className="scale-125 data-[state=checked]:bg-red-600"
            />
            <span className={`text-[10px] uppercase tracking-[0.1em] font-semibold ${mm?.enabled ? "text-red-700 dark:text-red-300" : "text-zinc-500"}`}>
              {mm?.enabled ? "ON — emails paused" : "Off"}
            </span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3">
          <div>
            <Label htmlFor="mm-reason" className="text-xs uppercase tracking-[0.08em] text-zinc-500">
              Reason (shown in the banner) <span className="text-zinc-400 normal-case">— optional, max 280 chars</span>
            </Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                id="mm-reason"
                value={mmReason}
                onChange={(e) => setMmReason(e.target.value.slice(0, 280))}
                placeholder="e.g. Migrating to new mail server — emails paused until 2 PM PT"
                data-testid="maintenance-mode-reason"
                disabled={mmSaving}
                className="flex-1"
              />
              <Button
                onClick={saveMaintenanceReason}
                disabled={mmSaving || !mm || (mmReason.trim() === (mm?.reason || "").trim())}
                data-testid="maintenance-mode-save-reason"
                variant="outline"
                className="h-10 shrink-0"
              >
                Save reason
              </Button>
            </div>
          </div>

          {mm?.updated_at && (
            <div className="text-[11px] text-zinc-500 dark:text-zinc-400 pt-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center gap-1.5 flex-wrap">
              <span>Last changed {new Date(mm.updated_at).toLocaleString()}</span>
              {mm.updated_by && <span>by <span className="font-mono">{mm.updated_by}</span></span>}
            </div>
          )}
        </div>
      </div>

      {/* -------- Login page branding (custom logo) -------- */}
      <div className="bg-white border border-zinc-200 rounded-md p-6 mt-8 max-w-3xl" id="section-login-logo" data-testid="login-logo-card">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-lg font-semibold text-zinc-950 dark:text-white">Login page branding</h2>
            </div>
            <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1 max-w-2xl">
              Upload a district logo (PNG/JPG/SVG/WebP, ≤ 1 MB) to appear centered on the login page's hero panel, between the HelpDesk badge and the tagline. Leave empty for the default look.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 items-start">
          <div className="border border-zinc-200 rounded-md bg-zinc-900 relative h-48 flex items-center justify-center overflow-hidden" data-testid="login-logo-preview">
            {loginLogo?.data_url ? (
              <img
                src={loginLogo.data_url}
                alt="Login logo preview"
                className="max-h-full max-w-full object-contain p-3"
              />
            ) : (
              <div className="text-center text-[11px] text-zinc-400 px-4">
                <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No logo — hero panel uses the default HelpDesk look
              </div>
            )}
            <div className="absolute top-1 left-2 text-[9px] uppercase tracking-[0.15em] text-zinc-400">Preview (hero bg)</div>
          </div>

          <div className="space-y-3">
            <label
              htmlFor="login-logo-input"
              data-testid="login-logo-upload-label"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white text-sm font-medium cursor-pointer transition-colors"
            >
              <Plus className="h-4 w-4" />
              {loginLogo?.data_url ? "Replace logo…" : "Upload logo…"}
            </label>
            <input
              id="login-logo-input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onLoginLogoPicked(f); }}
              disabled={logoUploading}
              data-testid="login-logo-file-input"
            />

            {loginLogo?.data_url && (
              <Button
                variant="outline"
                onClick={removeLoginLogo}
                disabled={logoUploading}
                data-testid="login-logo-remove-btn"
                className="h-9 text-xs"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Remove logo
              </Button>
            )}

            <ul className="text-[11px] text-zinc-500 space-y-0.5 mt-2">
              <li>· Recommended: transparent PNG or SVG</li>
              <li>· Renders on dark hero — light / transparent artwork works best</li>
              <li>· Max height on login page: 224px, keeps its aspect ratio</li>
              <li>· Publicly readable (login page is pre-auth) — don't upload sensitive imagery</li>
            </ul>

            {loginLogo?.updated_at && (
              <div className="text-[10px] text-zinc-500 pt-2 border-t border-zinc-100">
                Last updated {new Date(loginLogo.updated_at).toLocaleString()} by {loginLogo.updated_by || "—"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SSO card */}
      <div id="section-sso" data-testid="sso-card" className="mt-8 bg-white border border-zinc-200 rounded-md p-6 max-w-3xl">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-lg font-semibold text-zinc-950">Microsoft Entra ID (Azure AD) SSO</h2>
            </div>
            <p className="text-sm text-zinc-500 mt-1">
              When enabled, the login page shows a <strong>Continue with Microsoft</strong> button. New users are auto-created as <strong>User</strong> and can be promoted by an admin.
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="sso-toggle-wrap">
            <Switch checked={!!form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="sso-enabled-switch" />
            <span className="text-sm text-zinc-700">{form.enabled ? "Enabled" : "Disabled"}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Tenant ID</Label>
            <Input value={form.tenant_id} onChange={(e) => set("tenant_id", e.target.value)} placeholder="e.g. 00000000-0000-0000-0000-000000000000" data-testid="sso-tenant-input" className="h-11 rounded-md font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Client ID (Application ID)</Label>
            <Input value={form.client_id} onChange={(e) => set("client_id", e.target.value)} placeholder="Azure AD application (client) ID" data-testid="sso-client-input" className="h-11 rounded-md font-mono text-xs" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">
              Client Secret {sso?.has_secret && <span className="text-emerald-600 ml-1">(saved: {sso.client_secret_masked})</span>}
            </Label>
            <Input
              value={form.client_secret}
              onChange={(e) => set("client_secret", e.target.value)}
              placeholder={sso?.has_secret ? "Leave blank to keep the current secret" : "Paste the secret value from Azure"}
              type="password"
              data-testid="sso-secret-input"
              className="h-11 rounded-md font-mono text-xs"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Redirect URI (register this exact string in Azure)</Label>
            <Input value={form.redirect_uri} onChange={(e) => set("redirect_uri", e.target.value)} data-testid="sso-redirect-input" className="h-11 rounded-md font-mono text-xs" />
            {sso?.suggested_redirect_uri && (
              <div className="text-[11px] text-zinc-500">
                Suggested: <button type="button" onClick={() => set("redirect_uri", sso.suggested_redirect_uri)} className="font-mono text-[#0055FF] hover:text-[#0040CC] underline underline-offset-2">{sso.suggested_redirect_uri}</button>
              </div>
            )}
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Allowed email domains</Label>
            <Input
              value={form.allowed_domains}
              onChange={(e) => set("allowed_domains", e.target.value)}
              placeholder="esusd.org  (comma-separated; leave blank to allow every domain)"
              data-testid="sso-allowed-domains-input"
              className="h-11 rounded-md font-mono text-xs"
            />
            <p className="text-[11px] text-zinc-500">
              When set, only Microsoft accounts whose email ends with one of these domains can sign in via SSO. Leave blank to accept every domain in your tenant.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="sso-save-button"
            className="bg-[#0055FF] hover:bg-[#0040CC] text-white h-11 rounded-md transition-colors duration-200"
          >
            {saving ? "Saving…" : "Save settings"}
          </Button>
          <Button
            variant="outline"
            onClick={test}
            disabled={testing || !sso?.has_secret && !form.client_secret}
            data-testid="sso-test-button"
            className="h-11 rounded-md"
          >
            <PlugZap className="h-4 w-4 mr-2" />
            {testing ? "Testing…" : "Test connection"}
          </Button>
          <a
            href="https://entra.microsoft.com"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1"
          >
            Open Entra admin center <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {testResult && (
          <div className={`mt-4 rounded-md border p-4 text-sm ${testResult.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="sso-test-result">
            <div className="flex items-center gap-2 font-medium">
              {testResult.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
              {testResult.ok ? "Metadata reachable" : "Test failed"}
            </div>
            {testResult.ok ? (
              <ul className="mt-2 text-xs text-zinc-700 font-mono space-y-0.5 break-all">
                <li>issuer: {testResult.issuer}</li>
                <li>authorize: {testResult.authorization_endpoint}</li>
                <li>token: {testResult.token_endpoint}</li>
                <li>redirect_uri: {testResult.redirect_uri_configured}</li>
              </ul>
            ) : (
              <div className="mt-1 text-xs text-red-700 font-mono break-all">{testResult.detail}</div>
            )}
            {testResult.ok && (
              <div className="mt-3 text-xs text-zinc-600">{testResult.note}</div>
            )}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 mb-2">Setup checklist</div>
          <ol className="text-sm text-zinc-700 space-y-1.5 list-decimal pl-5">
            <li>Register an app in <strong>Entra admin center → App registrations</strong>, single-tenant.</li>
            <li>Under <strong>Authentication → Add a platform → Web</strong>, add the Redirect URI above <em>exactly</em>.</li>
            <li>Under <strong>Certificates & secrets</strong>, generate a new client secret and paste the <em>value</em> above.</li>
            <li>Fill Tenant ID + Client ID, save, then click <strong>Test connection</strong>.</li>
            <li>Toggle <strong>Enabled</strong> on — a Microsoft button will appear on the login page.</li>
          </ol>
        </div>
      </div>

      {/* -------- SSO Group Sync — Entra ID Distribution Groups → sites/roles -------- */}
      {ssoSyncSchedule && (
      <div className="bg-white border border-zinc-200 rounded-md p-6 mt-8 max-w-3xl" id="section-sso-sync" data-testid="sso-sync-card">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-lg font-semibold text-zinc-950 dark:text-white">Entra ID group sync</h2>
            </div>
            <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1 max-w-2xl">
              Auto-provision users from Microsoft Entra ID Distribution Groups and map each group to a site + optional role. <strong>Mirror mode</strong>: SSO-provisioned users who leave a mapped group have their sites cleared on next sync. Requires Graph app permissions <code className="text-[11px]">Group.Read.All</code>, <code className="text-[11px]">GroupMember.Read.All</code>, <code className="text-[11px]">User.Read.All</code> (admin-consented).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={!!ssoSyncSchedule.enabled}
              onCheckedChange={(v) => updateSsoSchedule({ enabled: v })}
              data-testid="sso-sync-schedule-switch"
            />
            <span className="text-sm text-zinc-700 dark:text-white">
              Nightly {ssoSyncSchedule.enabled ? "on" : "off"}
            </span>
          </div>
        </div>

        {/* Graph status pill */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="sso-graph-status">
          {ssoGraphStatus?.ok ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-1 text-[11px] font-semibold">
              <CheckCircle2 className="h-3 w-3" /> Graph reachable · {ssoGraphStatus.tenant_display_name || "connected"}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-[11px] font-semibold" title={ssoGraphStatus?.detail || ""}>
              <XCircle className="h-3 w-3" /> Graph unreachable — grant Graph app permissions
            </span>
          )}
          <Button variant="outline" size="sm" onClick={recheckGraph} className="h-7 text-xs" data-testid="sso-graph-recheck">
            <RefreshCw className="h-3 w-3 mr-1" /> Recheck
          </Button>
        </div>

        {/* Group search + add */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mb-3">
          <Input
            value={ssoSyncGroupSearch}
            onChange={(e) => setSsoSyncGroupSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchSsoGroups()}
            placeholder="Search Entra groups by name or email (e.g. LVHS, District)"
            data-testid="sso-group-search-input"
            className="h-11 rounded-md font-mono text-xs"
          />
          <Button onClick={searchSsoGroups} disabled={ssoSyncSearching} data-testid="sso-group-search-btn" className="h-11 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white">
            {ssoSyncSearching ? "Searching…" : "Search groups"}
          </Button>
        </div>
        {ssoSyncSearchResults.length > 0 && (
          <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 mb-4">
            {ssoSyncSearchResults.map((g) => (
              <div key={g.id} className="flex items-center gap-3 px-3 py-2 text-xs hover:bg-zinc-50" data-testid={`sso-group-hit-${g.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-800 font-medium truncate">{g.displayName}</div>
                  <div className="text-[10px] text-zinc-500 font-mono truncate">{g.mail || "—"} · {g.id}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => addSsoGroupMap(g)} className="h-7 text-xs">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Existing mappings */}
        <div className="border border-zinc-200 rounded-md overflow-hidden" data-testid="sso-mappings-table">
          <div className="grid grid-cols-[1fr_200px_140px_60px_40px] gap-2 px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-[0.05em] text-zinc-500 font-semibold">
            <div>Entra Group</div>
            <div>Sites</div>
            <div>Role escalation</div>
            <div className="text-center">On</div>
            <div></div>
          </div>
          {ssoSyncMaps.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-zinc-500 italic">
              No mappings yet — search above and click <strong>Add</strong>.
            </div>
          ) : ssoSyncMaps.map((m) => {
            const rowSites = m.sites || (m.site ? [m.site] : []);
            return (
            <div key={m.id} className="grid grid-cols-[1fr_200px_140px_60px_40px] gap-2 items-center px-3 py-2 border-t border-zinc-100 text-xs" data-testid={`sso-map-row-${m.id}`}>
              <div className="min-w-0">
                <div className="text-zinc-800 font-medium truncate">{m.group_display_name}</div>
                <div className="text-[10px] text-zinc-500 font-mono truncate">{m.group_id}</div>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    data-testid={`sso-map-sites-${m.id}`}
                    className="h-8 rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 px-2 text-xs text-left flex items-center justify-between gap-1 hover:border-zinc-400 dark:hover:border-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#0055FF]/25"
                  >
                    <span className="truncate">
                      {rowSites.length === 0 ? <span className="text-zinc-400 dark:text-zinc-500 italic">— none —</span>
                        : rowSites.length <= 2 ? rowSites.join(", ")
                        : <>{rowSites.slice(0,2).join(", ")} <span className="text-zinc-500 dark:text-zinc-400">+{rowSites.length - 2}</span></>}
                    </span>
                    <ChevronDown className="h-3 w-3 text-zinc-400 dark:text-zinc-500 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700" align="start">
                  <div className="text-[10px] uppercase tracking-[0.05em] text-zinc-500 dark:text-zinc-400 font-semibold px-2 pb-1.5">Assign sites</div>
                  <div className="max-h-64 overflow-auto space-y-0.5">
                    {SITE_CODES.map((s) => {
                      const on = rowSites.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => {
                            const next = on ? rowSites.filter((x) => x !== s) : [...rowSites, s];
                            patchSsoMap(m.id, { sites: next });
                          }}
                          data-testid={`sso-map-site-option-${m.id}-${s}`}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${on ? "bg-[#0055FF]/10 text-[#0055FF] dark:bg-[#0055FF]/20 dark:text-blue-200 font-medium" : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200"}`}
                        >
                          <div className={`h-3.5 w-3.5 rounded-sm border flex items-center justify-center shrink-0 ${on ? "bg-[#0055FF] border-[#0055FF]" : "border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800"}`}>
                            {on && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3.5} />}
                          </div>
                          <span className="font-mono">{s}</span>
                        </button>
                      );
                    })}
                  </div>
                  {rowSites.length > 0 && (
                    <button
                      type="button"
                      onClick={() => patchSsoMap(m.id, { sites: [] })}
                      className="w-full mt-1 px-2 py-1 rounded text-[10px] text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 border-t border-zinc-100 dark:border-zinc-700 pt-1.5"
                    >
                      Clear all
                    </button>
                  )}
                </PopoverContent>
              </Popover>
              <select
                value={m.role || ""}
                onChange={(e) => patchSsoMap(m.id, { role: e.target.value || null })}
                data-testid={`sso-map-role-${m.id}`}
                className="h-8 rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 px-2 text-xs"
              >
                <option value="">— default (user) —</option>
                {ROLE_LIST.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <div className="flex justify-center">
                <Switch
                  checked={!!m.enabled}
                  onCheckedChange={(v) => patchSsoMap(m.id, { enabled: v })}
                  data-testid={`sso-map-enabled-${m.id}`}
                />
              </div>
              <button
                onClick={() => deleteSsoMap(m.id)}
                data-testid={`sso-map-delete-${m.id}`}
                className="text-zinc-400 hover:text-red-600 flex items-center justify-center"
                title="Remove mapping"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );})}
        </div>

        {/* Schedule + run controls */}
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Button
            variant="outline"
            onClick={previewSsoSync}
            disabled={ssoSyncPreviewing || ssoSyncMaps.filter((m) => m.enabled).length === 0}
            data-testid="sso-sync-preview-btn"
            className="h-11 rounded-md"
          >
            {ssoSyncPreviewing ? "Previewing…" : "Preview (dry run)"}
          </Button>
          <Button
            onClick={runSsoSync}
            disabled={ssoSyncRunning || ssoSyncMaps.filter((m) => m.enabled).length === 0}
            data-testid="sso-sync-run-btn"
            className="bg-[#0055FF] hover:bg-[#0040CC] text-white h-11 rounded-md"
          >
            <PlayCircle className="h-4 w-4 mr-2" />
            {ssoSyncRunning ? "Syncing…" : "Run sync now"}
          </Button>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-zinc-500">Nightly UTC hour</Label>
            <Input
              type="number"
              min="0"
              max="23"
              value={ssoSyncSchedule.run_at_hour_utc}
              onChange={(e) => updateSsoSchedule({ run_at_hour_utc: parseInt(e.target.value, 10) })}
              data-testid="sso-sync-hour-input"
              className="h-9 w-20 rounded-md font-mono text-xs"
            />
          </div>
          {ssoSyncSchedule.last_run_at && (
            <span className="text-[10px] text-zinc-500 ml-auto">
              Last run {new Date(ssoSyncSchedule.last_run_at).toLocaleString()}
              {ssoSyncSchedule.last_run_result && (
                <> · +{ssoSyncSchedule.last_run_result.created} / ~{ssoSyncSchedule.last_run_result.updated} / −{ssoSyncSchedule.last_run_result.cleared}</>
              )}
            </span>
          )}
        </div>

        {/* Preview panel */}
        {ssoSyncPreview && (
          <div className="mt-5 border-2 border-dashed border-[#0055FF] rounded-md p-4 bg-blue-50/40" data-testid="sso-preview-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#0055FF] font-semibold">Dry-run preview</div>
                <h3 className="text-sm font-semibold text-zinc-950">Would apply on next real sync</h3>
                <p className="text-[11px] text-zinc-500">Nothing has been written yet. Click <strong>Run sync now</strong> below to commit these changes.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={runSsoSync}
                  disabled={ssoSyncRunning || (ssoSyncPreview.created + ssoSyncPreview.updated + ssoSyncPreview.cleared) === 0}
                  data-testid="sso-preview-apply-btn"
                  className="h-8 bg-[#0055FF] hover:bg-[#0040CC] text-white text-xs"
                >
                  Apply now →
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSsoSyncPreview(null)}
                  data-testid="sso-preview-dismiss-btn"
                  className="h-8 text-xs"
                >
                  Dismiss
                </Button>
              </div>
            </div>

            {!ssoSyncPreview.ok && ssoSyncPreview.errors && ssoSyncPreview.errors.length > 0 && (
              <div className="mb-3 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2 font-mono">
                {ssoSyncPreview.errors[0]}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Created */}
              <div className="border border-emerald-200 bg-white rounded-md" data-testid="sso-preview-created">
                <div className="px-3 py-2 border-b border-emerald-100 bg-emerald-50 text-[10px] uppercase tracking-[0.05em] font-semibold text-emerald-800">
                  Create ({ssoSyncPreview.preview?.created?.length || 0})
                </div>
                {(ssoSyncPreview.preview?.created || []).length === 0 ? (
                  <div className="px-3 py-4 text-[11px] text-zinc-400 italic text-center">No new users</div>
                ) : (
                  <ul className="divide-y divide-emerald-50 max-h-56 overflow-y-auto">
                    {(ssoSyncPreview.preview?.created || []).map((u) => (
                      <li key={u.email} className="px-3 py-2 text-[11px]">
                        <div className="font-medium text-zinc-800 truncate">{u.name}</div>
                        <div className="text-[10px] text-zinc-500 font-mono truncate">{u.email}</div>
                        <div className="text-[10px] text-zinc-600 mt-1">
                          role <span className="font-mono bg-zinc-100 px-1 rounded">{u.role}</span> · sites{" "}
                          <span className="font-mono">{(u.sites || []).join(", ") || "—"}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Updated */}
              <div className="border border-zinc-200 bg-white rounded-md" data-testid="sso-preview-updated">
                <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50 text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700">
                  Update ({ssoSyncPreview.preview?.updated?.length || 0})
                </div>
                {(ssoSyncPreview.preview?.updated || []).length === 0 ? (
                  <div className="px-3 py-4 text-[11px] text-zinc-400 italic text-center">Nothing changed</div>
                ) : (
                  <ul className="divide-y divide-zinc-50 max-h-56 overflow-y-auto">
                    {(ssoSyncPreview.preview?.updated || []).map((u) => (
                      <li key={u.email} className="px-3 py-2 text-[11px]">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-mono text-[10px] text-zinc-700 truncate flex-1">{u.email}</div>
                          <span className="text-[9px] uppercase tracking-[0.05em] px-1 py-0.5 rounded bg-zinc-200 text-zinc-700 shrink-0">
                            {u.reason}
                          </span>
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                          role: <span className="text-zinc-800">{u.from_role}</span>
                          {u.from_role !== u.to_role && <> → <span className="text-emerald-700 font-semibold">{u.to_role}</span></>}
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono">
                          sites: <span className="text-zinc-800">{(u.from_sites || []).join(",") || "—"}</span> → <span className="text-emerald-700 font-semibold">{(u.to_sites || []).join(",") || "—"}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Cleared */}
              <div className="border border-amber-200 bg-white rounded-md" data-testid="sso-preview-cleared">
                <div className="px-3 py-2 border-b border-amber-100 bg-amber-50 text-[10px] uppercase tracking-[0.05em] font-semibold text-amber-800">
                  Clear ({ssoSyncPreview.preview?.cleared?.length || 0})
                </div>
                {(ssoSyncPreview.preview?.cleared || []).length === 0 ? (
                  <div className="px-3 py-4 text-[11px] text-zinc-400 italic text-center">Nobody dropped</div>
                ) : (
                  <ul className="divide-y divide-amber-50 max-h-56 overflow-y-auto">
                    {(ssoSyncPreview.preview?.cleared || []).map((u) => (
                      <li key={u.email} className="px-3 py-2 text-[11px]">
                        <div className="font-mono text-[10px] text-zinc-700 truncate">{u.email}</div>
                        <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                          was: role <span className="bg-zinc-100 px-1 rounded">{u.was_role}</span> · sites <span className="bg-zinc-100 px-1 rounded">{(u.was_sites || []).join(",") || "—"}</span>
                        </div>
                        <div className="text-[10px] text-amber-700 font-semibold mt-1">→ sites cleared, role → user</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sync log */}
        <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-700" data-testid="sso-sync-log-section">
          <h3 className="font-heading text-sm font-semibold text-zinc-950 dark:text-white mb-2">Recent sync runs</h3>
          {ssoSyncLog.length === 0 ? (
            <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md py-4 text-center">
              No syncs yet — click <strong>Run sync now</strong> to populate.
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 overflow-hidden">
              {ssoSyncLog.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-xs" data-testid={`sso-sync-log-row-${r.id}`}>
                  {r.ok
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    : <XCircle className="h-3.5 w-3.5 text-red-600 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-zinc-800 dark:text-white">
                      <span className="font-mono text-[10px] bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded mr-1">+{r.created}</span>
                      <span className="font-mono text-[10px] bg-zinc-200 text-zinc-800 px-1 py-0.5 rounded mr-1">~{r.updated}</span>
                      <span className="font-mono text-[10px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded mr-2">−{r.cleared}</span>
                      by {r.actor}
                    </div>
                    {r.errors && r.errors.length > 0 && (
                      <div className="text-[10px] text-red-600 font-mono truncate mt-0.5" title={r.errors.join(" · ")}>
                        {r.errors[0]}
                      </div>
                    )}
                    {r.note && <div className="text-[10px] text-zinc-500 mt-0.5">{r.note}</div>}
                  </div>
                  <div className="text-[10px] text-zinc-500 shrink-0 tabular-nums">
                    {new Date(r.started_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* -------- SMTP Smarthost -------- */}
      {smtp && (
      <div className="bg-white border border-zinc-200 rounded-md p-6 mt-6 max-w-3xl" id="section-smtp" data-testid="smtp-card">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-lg font-semibold text-zinc-950 dark:text-white">SMTP smarthost (email alerts)</h2>
            </div>
            <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1 max-w-2xl">
              Route outbound email through your district's mail relay. Standard ports: <strong>587</strong> STARTTLS, <strong>465</strong> SSL, <strong>25</strong> plain (internal only). Leave username &amp; password blank for anonymous smarthosts.
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="smtp-toggle-wrap">
            <Switch
              checked={!!smtpForm.enabled}
              onCheckedChange={(v) => setSmtpForm((f) => ({ ...f, enabled: v }))}
              data-testid="smtp-enabled-switch"
            />
            <span className="text-sm text-zinc-700 dark:text-white">{smtpForm.enabled ? "Enabled" : "Disabled"}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">SMTP host</Label>
            <Input
              value={smtpForm.host}
              onChange={(e) => setSmtpForm((f) => ({ ...f, host: e.target.value }))}
              placeholder="smtp.office365.com  |  smtp.gmail.com  |  relay.district.local"
              data-testid="smtp-host-input"
              className="h-11 rounded-md font-mono text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Port</Label>
            <Input
              type="number"
              min="1"
              max="65535"
              value={smtpForm.port}
              onChange={(e) => setSmtpForm((f) => ({ ...f, port: e.target.value }))}
              placeholder="587"
              data-testid="smtp-port-input"
              className="h-11 rounded-md font-mono text-xs"
            />
          </div>

          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Sender email (From)</Label>
            <Input
              type="email"
              value={smtpForm.from_email}
              onChange={(e) => setSmtpForm((f) => ({ ...f, from_email: e.target.value }))}
              placeholder="helpdesk@esusd.org"
              data-testid="smtp-from-email-input"
              className="h-11 rounded-md font-mono text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Sender name (display)</Label>
            <Input
              value={smtpForm.from_name}
              onChange={(e) => setSmtpForm((f) => ({ ...f, from_name: e.target.value }))}
              placeholder="ESUSD HelpDesk"
              data-testid="smtp-from-name-input"
              className="h-11 rounded-md text-xs"
            />
          </div>

          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Username</Label>
            <Input
              value={smtpForm.username}
              onChange={(e) => setSmtpForm((f) => ({ ...f, username: e.target.value }))}
              placeholder="Leave blank for anonymous relay"
              data-testid="smtp-username-input"
              autoComplete="off"
              className="h-11 rounded-md font-mono text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">
              Password {smtp.has_password && <span className="text-emerald-600 ml-1">(saved: {smtp.password_masked})</span>}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="password"
                value={smtpForm.password}
                onChange={(e) => setSmtpForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={smtp.has_password ? "Leave blank to keep current" : "SMTP AUTH password"}
                data-testid="smtp-password-input"
                autoComplete="new-password"
                className="h-11 rounded-md font-mono text-xs flex-1"
              />
              {smtp.has_password && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm("Clear the saved SMTP password? Use this for port-25 anonymous relay where the smarthost trusts your egress IP instead of a login.")) return;
                    try {
                      const { data } = await api.put("/settings/smtp", { clear_password: true });
                      setSmtp(data);
                      setSmtpForm((f) => ({ ...f, password: "" }));
                      toast.success("SMTP password cleared — relay will now connect anonymously");
                    } catch (err) {
                      toast.error(formatApiError(err) || "Could not clear password");
                    }
                  }}
                  data-testid="smtp-password-clear-btn"
                  className="h-11 shrink-0 text-xs whitespace-nowrap"
                >
                  Clear saved
                </Button>
              )}
            </div>
          </div>

          <div className="md:col-span-3 flex flex-wrap items-center gap-6">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Security</Label>
              <div className="inline-flex items-center border border-zinc-300 rounded-md overflow-hidden text-xs" data-testid="smtp-security-toggle">
                {[
                  { k: "starttls", label: "STARTTLS · 587" },
                  { k: "ssl",      label: "SSL · 465" },
                  { k: "none",     label: "None · 25" },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setSmtpForm((f) => ({ ...f, security: opt.k, port: f.port || (opt.k === "ssl" ? 465 : opt.k === "none" ? 25 : 587) }))}
                    data-testid={`smtp-security-${opt.k}`}
                    className={`px-3 py-2 transition-colors duration-150 ${smtpForm.security === opt.k ? "bg-zinc-950 text-white" : "text-zinc-700 hover:bg-zinc-50"}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1 flex-1 min-w-[220px]">
              <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Send test to</Label>
              <Input
                type="email"
                value={smtpForm.test_to}
                onChange={(e) => setSmtpForm((f) => ({ ...f, test_to: e.target.value }))}
                placeholder="Defaults to your admin email"
                data-testid="smtp-test-to-input"
                className="h-11 rounded-md font-mono text-xs"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Button
            onClick={saveSmtp}
            disabled={smtpSaving}
            data-testid="smtp-save-button"
            className="bg-[#0055FF] hover:bg-[#0040CC] text-white h-11 rounded-md transition-colors duration-200"
          >
            {smtpSaving ? "Saving…" : "Save SMTP settings"}
          </Button>
          <Button
            variant="outline"
            onClick={testSmtp}
            disabled={smtpTesting || !smtpForm.host || !smtpForm.from_email}
            data-testid="smtp-test-button"
            className="h-11 rounded-md"
          >
            <PlugZap className="h-4 w-4 mr-2" />
            {smtpTesting ? "Sending…" : "Send test email"}
          </Button>
          {smtp.updated_at && (
            <span className="text-[10px] text-zinc-500 ml-auto">
              Last saved {new Date(smtp.updated_at).toLocaleString()} by {smtp.updated_by || "—"}
            </span>
          )}
        </div>

        {/* -------- Delivery routes -------- */}
        <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-700" data-testid="smtp-routes-section">
          <h3 className="font-heading text-sm font-semibold text-zinc-950 dark:text-white mb-1">Delivery routes</h3>
          <p className="text-[11px] text-zinc-500 dark:text-zinc-300 mb-4">
            Emails try SMTP smarthost first. When SMTP is disabled, blank, or fails, the app can either fall back to the Emergent-managed Resend proxy or fail the send outright.
          </p>

          <div className="flex items-start justify-between gap-4 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950/40 p-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Cloud className="h-4 w-4 text-zinc-500 shrink-0" strokeWidth={2.2} />
                <div className="text-sm font-semibold text-zinc-950 dark:text-white">Emergent proxy fallback</div>
                {smtpForm.emergent_proxy_enabled ? (
                  <span className="inline-flex items-center text-[10px] uppercase tracking-[0.12em] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 px-2 py-0.5 rounded-full">On</span>
                ) : (
                  <span className="inline-flex items-center text-[10px] uppercase tracking-[0.12em] font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 px-2 py-0.5 rounded-full">Off</span>
                )}
              </div>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-300 mt-1.5 leading-snug max-w-xl">
                When <strong>on</strong>, ESUSD's Emergent-managed Resend proxy delivers any email your smarthost couldn't send. When <strong>off</strong>, only the SMTP smarthost above is used — if it fails or isn't configured, the send is dropped and logged as <code>skipped</code>. Use "off" when your district policy forbids third-party mail relays.
              </p>
            </div>
            <Switch
              checked={!!smtpForm.emergent_proxy_enabled}
              onCheckedChange={(v) => setSmtpForm((f) => ({ ...f, emergent_proxy_enabled: !!v }))}
              data-testid="smtp-emergent-proxy-toggle"
              className="scale-110 shrink-0 mt-1 data-[state=unchecked]:bg-red-300 dark:data-[state=unchecked]:bg-red-950/60"
            />
          </div>
        </div>

        {smtpTestResult && (
          <div
            className={`mt-4 rounded-md border p-4 text-sm ${smtpTestResult.ok ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
            data-testid="smtp-test-result"
          >
            <div className="flex items-center gap-2 font-medium">
              {smtpTestResult.ok
                ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                : <XCircle className="h-4 w-4 text-red-600" />}
              {smtpTestResult.ok ? "Test email sent" : "SMTP test failed"}
            </div>
            {smtpTestResult.ok ? (
              <ul className="mt-2 text-xs text-zinc-700 font-mono space-y-0.5 break-all">
                <li>to: {smtpTestResult.sent_to}</li>
                <li>host: {smtpTestResult.host}:{smtpTestResult.port}</li>
                <li>security: {smtpTestResult.security}</li>
              </ul>
            ) : (
              <div className="mt-1 text-xs text-red-700 font-mono break-all">{smtpTestResult.detail}</div>
            )}
          </div>
        )}

        {/* -------- Send log (last 20) -------- */}
        <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-700" data-testid="smtp-log-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-heading text-sm font-semibold text-zinc-950 dark:text-white">Recent alert sends</h3>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-300 mt-0.5">
                Last 20 delivery attempts. <span className="inline-flex items-center gap-1"><span className="inline-block h-1.5 w-1.5 rounded-full bg-[#0055FF]" /> smarthost</span> · <span className="inline-flex items-center gap-1"><span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" /> proxy fallback</span>
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshSmtpLog}
              disabled={smtpLogRefreshing}
              data-testid="smtp-log-refresh"
              className="h-8 text-xs"
            >
              {smtpLogRefreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>

          {smtpLog.length === 0 ? (
            <div className="text-xs text-zinc-500 dark:text-zinc-400 italic border border-dashed border-zinc-200 dark:border-zinc-700 rounded-md py-6 text-center">
              No email attempts yet. Create a ticket or send a test above to populate this log.
            </div>
          ) : (
            <div className="border border-zinc-200 dark:border-zinc-700 rounded-md divide-y divide-zinc-100 dark:divide-zinc-700 overflow-hidden">
              {smtpLog.map((e) => {
                const routeColor = e.route === "smtp"
                  ? "bg-[#0055FF] text-white"
                  : e.route === "emergent"
                    ? "bg-amber-500 text-white"
                    : "bg-zinc-400 text-white";
                const routeLabel = e.route === "smtp" ? "SMARTHOST" : e.route === "emergent" ? "PROXY" : "SKIPPED";
                return (
                  <div
                    key={e.id}
                    className="flex items-center gap-3 px-3 py-2 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800"
                    data-testid={`smtp-log-row-${e.id}`}
                  >
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold tracking-[0.05em] shrink-0 ${routeColor}`}
                      data-testid={`smtp-log-route-${e.id}`}
                    >
                      {routeLabel}
                    </span>
                    {e.ok
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      : <XCircle className="h-3.5 w-3.5 text-red-600 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-zinc-800 dark:text-white truncate" title={e.subject}>{e.subject}</div>
                      <div className="text-[10px] text-zinc-500 dark:text-zinc-400 font-mono truncate" title={e.detail}>
                        → {e.to} {e.detail ? `· ${e.detail}` : ""}
                      </div>
                    </div>
                    <div className="text-[10px] text-zinc-500 dark:text-zinc-400 shrink-0 tabular-nums">
                      {new Date(e.ts).toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      )}

      {/* -------- Cloudflare Reverse Proxy / Tunnel -------- */}
      {cf && (
      <div className="bg-white border border-zinc-200 rounded-md p-6 mt-8 max-w-3xl" id="section-cloudflare" data-testid="cloudflare-card">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Cloud className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-lg font-semibold text-zinc-950 dark:text-white">Cloudflare reverse proxy</h2>
            </div>
            <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1 max-w-2xl">
              When you host this on-prem with the Docker installer, paste a Cloudflare Tunnel token to expose it at your own <code className="text-[11px]">helpdesk.esusd.org</code> without opening ports. The Docker sidecar reads these values on boot.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={cfForm.enabled}
              onCheckedChange={(v) => setCfForm((f) => ({ ...f, enabled: v }))}
              data-testid="cf-enabled-switch"
            />
            <span className="text-sm text-zinc-700 dark:text-white">{cfForm.enabled ? "Enabled" : "Disabled"}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-zinc-950 dark:text-white">Public hostname</Label>
            <Input
              value={cfForm.public_hostname}
              onChange={(e) => setCfForm((f) => ({ ...f, public_hostname: e.target.value }))}
              placeholder="helpdesk.esusd.org"
              data-testid="cf-hostname-input"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-950 dark:text-white">Tunnel token</Label>
            <Input
              type="password"
              value={cfForm.tunnel_token}
              onChange={(e) => setCfForm((f) => ({ ...f, tunnel_token: e.target.value }))}
              placeholder={cf.tunnel_token_set ? "Leave to keep existing" : "Paste cloudflared tunnel token"}
              data-testid="cf-token-input"
              className="mt-1 font-mono text-xs"
            />
            <p className="text-[10px] text-zinc-500 mt-1">
              Never displayed after saving. {cf.tunnel_token_set ? "A token is currently on file." : "No token on file."}
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-zinc-950 dark:text-white">Additional CORS origins (comma-separated)</Label>
            <Input
              value={cfForm.additional_origins}
              onChange={(e) => setCfForm((f) => ({ ...f, additional_origins: e.target.value }))}
              placeholder="https://staff.esusd.org, https://intranet.esusd.org"
              data-testid="cf-origins-input"
              className="mt-1"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <Switch
              checked={cfForm.trust_cf_headers}
              onCheckedChange={(v) => setCfForm((f) => ({ ...f, trust_cf_headers: v }))}
              data-testid="cf-trust-switch"
            />
            <Label className="text-sm text-zinc-950 dark:text-white">Trust Cloudflare headers</Label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Button
            onClick={async () => {
              setCfSaving(true);
              try {
                const payload = {
                  enabled: cfForm.enabled,
                  public_hostname: cfForm.public_hostname.trim(),
                  trust_cf_headers: cfForm.trust_cf_headers,
                  additional_origins: (cfForm.additional_origins || "")
                    .split(",").map((s) => s.trim()).filter(Boolean),
                };
                if (cfForm.tunnel_token && !cfForm.tunnel_token.startsWith("•")) {
                  payload.tunnel_token = cfForm.tunnel_token.trim();
                }
                const { data } = await api.put("/settings/cloudflare", payload);
                setCf(data);
                setCfForm((f) => ({ ...f, tunnel_token: data.tunnel_token_masked || "" }));
                toast.success("Cloudflare settings saved");
              } catch (e) { toast.error(formatApiError(e)); }
              finally { setCfSaving(false); }
            }}
            disabled={cfSaving}
            data-testid="cf-save-button"
            className="bg-[#0055FF] hover:bg-[#0040CC] text-white h-11 rounded-md transition-colors duration-200"
          >
            {cfSaving ? "Saving…" : "Save Cloudflare settings"}
          </Button>
          {cf.updated_at && (
            <span className="text-[10px] text-zinc-500 ml-auto">
              Last saved {new Date(cf.updated_at).toLocaleString()} by {cf.updated_by || "—"}
            </span>
          )}
        </div>
      </div>
      )}
      {/* Yearly archive card */}
      <div className="mt-8 bg-white border border-zinc-200 rounded-md p-6 max-w-3xl" id="section-archive" data-testid="archive-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Archive className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-lg font-semibold text-zinc-950">Yearly ticket archive</h2>
            </div>
            <p className="text-sm text-zinc-500 mt-1">
              Once a year on the date below, tickets are automatically archived and hidden from queues.
              Archived tickets are preserved in the database and audited — you can still surface them later if needed.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!archForm.enabled} onCheckedChange={(v) => setArchForm({ ...archForm, enabled: v })} data-testid="archive-enabled-switch" />
            <span className="text-sm text-zinc-700">{archForm.enabled ? "Enabled" : "Disabled"}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Month</Label>
            <select
              value={archForm.month}
              onChange={(e) => setArchForm({ ...archForm, month: parseInt(e.target.value, 10) })}
              data-testid="archive-month-select"
              className="h-11 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {MONTHS.map((m, i) => (<option key={m} value={i + 1}>{m}</option>))}
            </select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Day</Label>
            <select
              value={archForm.day}
              onChange={(e) => setArchForm({ ...archForm, day: parseInt(e.target.value, 10) })}
              data-testid="archive-day-select"
              className="h-11 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (<option key={d} value={d}>{d}</option>))}
            </select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Scope</Label>
            <div className="h-11 flex items-center gap-2 text-sm text-zinc-700">
              <Switch checked={!!archForm.only_closed} onCheckedChange={(v) => setArchForm({ ...archForm, only_closed: v })} data-testid="archive-only-closed-switch" />
              <span>Only archive Resolved/Closed tickets</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <Button onClick={saveArchive} disabled={archSaving} data-testid="archive-save-button" className="bg-[#0055FF] hover:bg-[#0040CC] text-white h-11 rounded-md">
            {archSaving ? "Saving…" : "Save schedule"}
          </Button>
          <Button variant="outline" onClick={runArchive} disabled={archRunning} data-testid="archive-run-now-button" className="h-11 rounded-md">
            <PlayCircle className="h-4 w-4 mr-2" />
            {archRunning ? "Archiving…" : "Run archive now"}
          </Button>
        </div>

        {arch && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div className="rounded-md border border-zinc-200 p-3">
              <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Next scheduled run</div>
              <div className="mt-1 font-medium text-zinc-950" data-testid="archive-next-run">{arch.next_run}</div>
            </div>
            <div className="rounded-md border border-zinc-200 p-3">
              <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Last run</div>
              <div className="mt-1 font-medium text-zinc-950">
                {arch.last_run_at ? new Date(arch.last_run_at).toLocaleDateString() : "—"}
              </div>
            </div>
            <div className="rounded-md border border-zinc-200 p-3">
              <div className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Tickets archived last run</div>
              <div className="mt-1 font-medium text-zinc-950 tabular-nums" data-testid="archive-last-count">{arch.last_run_count || 0}</div>
            </div>
          </div>
        )}
      </div>

      {/* -------- Website Maintenance: Backup & Restore -------- */}
      <div className="bg-white border border-zinc-200 rounded-md p-6 mt-8 max-w-3xl" id="section-backup" data-testid="maintenance-card">        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-lg font-semibold text-zinc-950 dark:text-white">Backup &amp; Restore</h2>
            </div>
            <p className="text-sm text-zinc-500 dark:text-zinc-300 mt-1 max-w-2xl">
              Download a full JSON snapshot of every collection (users, live tickets, per-year archives, audit log, settings, crests). Restore replaces existing data with the backup — use carefully.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={async () => {
              try {
                const r = await api.get("/admin/backup", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `helpdesk_backup_${new Date().toISOString().replace(/[:.]/g,"-")}.json`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("Backup downloaded");
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            data-testid="backup-download"
            className="h-11 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white"
          >Download backup</Button>

          <label className="inline-flex items-center h-11 px-4 rounded-md border border-zinc-300 text-zinc-800 hover:bg-zinc-50 cursor-pointer dark:text-white dark:border-zinc-600 dark:hover:bg-zinc-800" data-testid="restore-file-label">
            Restore from file
            <input
              type="file"
              accept="application/json,.json"
              className="hidden"
              data-testid="restore-file-input"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                if (!window.confirm(`Restore from ${f.name}? This REPLACES all current data (users, tickets, archives, audit log, settings). Continue?`)) return;
                if (!window.confirm("Are you absolutely sure? This action cannot be undone.")) return;
                try {
                  const text = await f.text();
                  const backup = JSON.parse(text);
                  const { data } = await api.post("/admin/restore", { confirm: true, backup });
                  const total = Object.values(data.restored || {}).reduce((s, n) => s + n, 0);
                  toast.success(`Restored ${total} documents · reload the page to see changes`);
                } catch (err) { toast.error(formatApiError(err) || "Restore failed"); }
              }}
            />
          </label>
        </div>
        <BackupScheduleSection />
      </div>
      {/* Bottom spacer so the last TOC targets (Archive, Backup) can scroll to the top */}
      <div aria-hidden="true" className="h-[70vh]" data-testid="settings-bottom-spacer" />
        </div>
      </div>

    </div>
  );
}
